/**
 * Visitor id for the store API, from inside the widget.
 *
 * ── Why the widget needs this ────────────────────────────────────────────────
 * The store API recognises an anonymous shopper's guest account by ONE thing:
 * the `x-visitor-id` header, a random id the browser keeps per site. It used to
 * use a fingerprint of device + browser + ip, which merged strangers behind one
 * mobile carrier into a single guest (shared cart, orders, addresses, chat) and
 * split one visitor into a new guest whenever their ip changed. See
 * salesive-store-api/src/utils/ghostIdentity.util.js.
 *
 * On a Salesive-hosted storefront the static server inlines a script into
 * <head> (salesive-static-server/src/assets/visitor-id.js) that mints the id
 * and tags every request to the store API, including the ones this widget
 * makes. That script announces itself on `window.__salesiveVisitorId`, and
 * when it is there this module does nothing: tagging twice would make the
 * XHR header "id, id", which the server rejects.
 *
 * On any other site (a merchant's own Wix or WordPress page embedding
 * widget.js) there is no inline script, so without this every widget request
 * carried no id and the shopper got a fresh guest per action: their chat and
 * their sign-in never survived a reload. So here the widget does the same job
 * itself, the narrow way: it keeps the id under the same localStorage key and
 * adds the header on the store API client the widget already uses. It never
 * patches XMLHttpRequest or fetch on somebody else's page, and it never touches
 * requests to the events service, whose CORS header list would reject it.
 */
import { api as salesiveApi } from "salesive-api-axios";

const KEY = "SALESIVE_VISITOR_ID";
const HEADER = "x-visitor-id";
// Mirrors normalizeVisitorId on the server.
const VALID = /^[A-Za-z0-9_-]{16,64}$/;

function randomId() {
    const c = typeof window !== "undefined" ? window.crypto : undefined;
    try {
        if (c && typeof c.randomUUID === "function") {
            return c.randomUUID().replace(/-/g, "");
        }
    } catch {
        /* fall through */
    }
    const bytes = new Uint8Array(16);
    try {
        if (c && typeof c.getRandomValues === "function") {
            c.getRandomValues(bytes);
        } else {
            throw new Error("no crypto");
        }
    } catch {
        for (let i = 0; i < 16; i++) bytes[i] = (Math.random() * 256) | 0;
    }
    let hex = "";
    for (let i = 0; i < 16; i++) {
        hex += (bytes[i] < 16 ? "0" : "") + bytes[i].toString(16);
    }
    return hex;
}

/** The id for this site, minting and storing one if needed. Null when storage is blocked. */
export function ensureVisitorId() {
    try {
        const store = window.localStorage;
        if (!store) return null;
        let id = store.getItem(KEY);
        if (!id || !VALID.test(id)) {
            id = randomId();
            store.setItem(KEY, id);
        }
        return id;
    } catch {
        return null;
    }
}

let installed = false;

/**
 * Attach the header to the store API client, unless the page's inline script
 * already handles it. Safe to call more than once. Returns what was done, for
 * the debug log.
 */
export function installVisitorId() {
    if (installed) return "already";
    if (typeof window === "undefined") return "no-window";

    // The static server's inline script is on this page: it tags every request
    // itself, ours included. Reuse its id and stay out of the way.
    if (window.__salesiveVisitorId && window.__salesiveVisitorId.installed) {
        installed = true;
        return "page-script";
    }

    const id = ensureVisitorId();
    if (!id) return "no-storage";

    try {
        window.SALESIVE_VISITOR_ID = id;
    } catch {
        /* ignore */
    }

    salesiveApi.interceptors.request.use((config) => {
        try {
            const headers = config.headers;
            if (headers && typeof headers.has === "function") {
                if (!headers.has(HEADER)) headers.set(HEADER, id);
            } else {
                config.headers = { ...(headers || {}), [HEADER]: id };
            }
        } catch {
            /* never break a request over this */
        }
        return config;
    });

    installed = true;
    return "widget";
}
